import { useLocation } from "react-router-dom";

import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import DefaultFooter from "examples/Footers/DefaultFooter";
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import HorizontalTeamCard from "examples/Cards/TeamCards/HorizontalTeamCard";
import DefaultInfoCard from "examples/Cards/InfoCards/DefaultInfoCard";

import footerRoutes from "footer.routes";
import routes from "routes";
import SectionHeading from "studio-edifique/components/SectionHeading";
import SeoHead from "studio-edifique/components/SeoHead";
import {
  company,
  createBreadcrumbSchema,
  createOrganizationSchema,
  heroImages,
  teamMembers,
  teamSectionIntro,
} from "studio-edifique/siteContent";

const synergyPoints = [
  {
    icon: "architecture",
    title: "Arquitetura com intenção",
    description:
      "Leitura estética, organização espacial e soluções pensadas para o uso real dos ambientes.",
  },
  {
    icon: "foundation",
    title: "Engenharia com critério",
    description:
      "Decisões técnicas voltadas a desempenho, compatibilização e viabilidade de execução.",
  },
  {
    icon: "groups",
    title: "Atuação complementar",
    description:
      "Uma equipe que integra conceito, documentação e leitura de obra para reduzir ruídos no processo.",
  },
];

function TeamPage() {
  const { pathname } = useLocation();

  return (
    <>
      <SeoHead
        title="Equipe Técnica"
        description="Conheça a equipe técnica da Studio Edifique e a formação de Sabrina Monticcelli e Gabriel Chagas."
        keywords={[
          ...company.seoKeywords,
          "equipe de arquitetura",
          "engenheiro e arquiteto em Itatiba",
        ]}
        path={pathname}
        image={heroImages.team}
        schema={[
          createOrganizationSchema(),
          createBreadcrumbSchema([
            { name: "Início", path: "/" },
            { name: "Time", path: pathname },
          ]),
        ]}
      />
      <DefaultNavbar
        brand={company.brandName}
        routes={routes}
        action={{
          type: "external",
          route: company.whatsappUrl,
          label: "Falar com a equipe",
          color: "info",
        }}
        transparent
        light
      />
      <MKBox
        minHeight="48vh"
        width="100%"
        sx={{
          backgroundImage: ({ functions: { linearGradient, rgba } }) =>
            `${linearGradient(rgba("#201E1D", 0.9), rgba("#111111", 0.52))}, url(${
              heroImages.team
            })`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <Grid
            container
            item
            xs={12}
            lg={8}
            justifyContent="center"
            sx={{ textAlign: "center", mx: "auto" }}
          >
            <MKTypography variant="button" textTransform="uppercase" letterSpacing={2} color="info">
              Equipe técnica
            </MKTypography>
            <MKTypography variant="h2" component="h1" color="white" mt={1}>
              Uma equipe pequena no tamanho e ampla na leitura técnica do processo.
            </MKTypography>
            <MKTypography variant="body1" color="white" opacity={0.84} mt={2}>
              {teamSectionIntro}
            </MKTypography>
          </Grid>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <MKBox
          component="section"
          variant="gradient"
          bgColor="dark"
          position="relative"
          py={8}
          px={{ xs: 2, lg: 0 }}
          mx={-2}
        >
          <Container>
            <Grid container>
              <Grid item xs={12} md={9} sx={{ mb: 6 }}>
                <SectionHeading
                  eyebrow="Equipe técnica"
                  title="Arquitetura e engenharia trabalhando com a mesma direção"
                  description="Os perfis abaixo foram organizados para apresentar formação, repertório técnico e foco de atuação."
                  light
                />
              </Grid>
            </Grid>
            <Grid container spacing={4}>
              {teamMembers.map((member) => (
                <Grid item xs={12} key={member.name}>
                  <HorizontalTeamCard
                    image={member.image}
                    name={member.name}
                    position={{ color: "info", label: member.role }}
                    description={member.description}
                  />
                </Grid>
              ))}
            </Grid>
          </Container>
        </MKBox>

        <MKBox component="section" py={8}>
          <Container>
            <Grid container spacing={4}>
              {teamMembers.map((member) => (
                <Grid item xs={12} lg={6} key={`${member.name}-credentials`}>
                  <Card sx={{ p: 3, height: "100%" }}>
                    <MKTypography variant="h5" mb={1}>
                      Formação de {member.name}
                    </MKTypography>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" mb={2}>
                      {member.highlights.map((highlight) => (
                        <Chip key={highlight} label={highlight} color="info" variant="outlined" />
                      ))}
                    </Stack>
                    <MKBox component="ul" pl={2.5} mb={0}>
                      {member.credentials.map((credential) => (
                        <MKTypography
                          key={credential}
                          component="li"
                          variant="body2"
                          color="text"
                          mb={1}
                        >
                          {credential}
                        </MKTypography>
                      ))}
                    </MKBox>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </MKBox>

        <MKBox
          component="section"
          py={8}
          sx={{
            background: ({ palette: { grey } }) => grey[100],
            mx: -2,
            px: { xs: 2, lg: 0 },
          }}
        >
          <Container>
            <SectionHeading
              eyebrow="Como a equipe atua"
              title="Integração entre projeto, detalhamento e obra"
              description="A força da Studio Edifique está na combinação entre intenção arquitetônica e leitura técnica de execução."
              align="center"
            />
            <Grid container spacing={4}>
              {synergyPoints.map((item) => (
                <Grid item xs={12} md={4} key={item.title}>
                  <DefaultInfoCard
                    icon={item.icon}
                    title={item.title}
                    description={item.description}
                  />
                </Grid>
              ))}
            </Grid>
          </Container>
        </MKBox>
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default TeamPage;
